import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { setMonthlyFlex } from 'actions/expiryRenewalActions';
import GreenCheckboxList, { Option } from 'components/GreenCheckboxList/GreenCheckboxList';
import { dispatch } from 'store';
import { Store } from 'types/store';
import { formatCurrency } from 'utils/formatPrice';
import RenewalPrice from 'views/MyRac/ExpiryRenewal/components/atoms/RenewalPrice';
import calculateBasketPricing from 'views/MyRac/ExpiryRenewal/utils/pricingUtils';
import { hasEuropeanRescue } from 'views/MyRac/ExpiryRenewal/utils/europeanRescueMonthlyFlex';

const BasketOptions = () => {
  const { isEligibleForMonthlyFlex, isMonthlyLongerTermPolicy } = useSelector((state: Store) => state.expiryRenewal.pageModel) ?? {};
  const {
    addOns, amendments, monthlyFlex, pageModel, removedExtras,
  } = useSelector((state: Store) => state.expiryRenewal) ?? {};

  const selectedAddOns = addOns?.selected ?? [];
  const isEurSelected = hasEuropeanRescue(selectedAddOns);

  const [selected, setSelected] = useState<Option[]>(pageModel?.isMonthlyLongerTermPolicy || (monthlyFlex && !isEurSelected) ? [{ id: 'monthly', label: 'Monthly' }] : [{ id: 'annual', label: 'Annual' }]);

  // Narrow on productCode too - "amendments" also carries ExtraPerson adds.
  const vehiclesAdded = (amendments ?? []).filter((a) => a.amendmentType === 'Add' && a.productCode === 'ExtraVehicle').length;
  const peopleAdded = (amendments ?? []).filter((a) => a.amendmentType === 'Add' && a.productCode === 'ExtraPerson').length;

  const annual = calculateBasketPricing(selectedAddOns, false, pageModel, vehiclesAdded, peopleAdded, removedExtras);
  const monthly = calculateBasketPricing(selectedAddOns, true, pageModel, vehiclesAdded, peopleAdded, removedExtras);

  const annualTotal = annual.totalPrice + annual.addOnTotal;
  const monthlyTotal = monthly.totalPrice + monthly.addOnTotal;

  // A policy paid upfront with a cover period other than 12 months isn't billed per year, so the /year suffix would be misleading here.
  const isNotAnnual = pageModel?.coverPeriod !== undefined && pageModel.coverPeriod !== 12;
  const annualPeriodLabel = isNotAnnual ? '' : '/year';

  const options = [
    {
      id: 'annual',
      label: 'Annual',
      description: (
        <>
          <div className="mt-2" />
          <RenewalPrice price={annualTotal} period={annualPeriodLabel} wasPrice={annual.wasTotal} />
        </>
      ),
    },
    {
      id: 'monthly',
      badge: isEligibleForMonthlyFlex ? 'NO EXTRA COST' : undefined,
      label: 'Monthly',
      description: (
        <>
          {isEligibleForMonthlyFlex && <div className="my-2">{`Billed monthly, cancel anytime. The same total for the year: ${formatCurrency(annualTotal)}`}</div>}
          <RenewalPrice price={monthlyTotal} period="/month" wasPrice={monthly.wasTotal} />
        </>
      ),
    },
  ];

  const option = options.filter(({ id }) => id === (isMonthlyLongerTermPolicy ? 'monthly' : 'annual'));
  // European Rescue and Monthly Flex are mutually exclusive, so Monthly is hidden here once European
  // Rescue is selected. The conflict modal lives on the Customise step, where the choice is still open.
  const visibleOptions = isEligibleForMonthlyFlex && !isEurSelected ? options : option;

  let heading = 'Your payment plan';
  let description = isMonthlyLongerTermPolicy && `Billed monthly. Total price over ${pageModel?.coverPeriod}-months: ${formatCurrency(annualTotal)}`;

  if (isEligibleForMonthlyFlex && !isEurSelected) {
    heading = 'One price, two ways to pay';
    description = 'You pay the same price whether you choose to pay monthly or annually.';
  } else if (isEligibleForMonthlyFlex && isEurSelected) {
    description = `As a reminder, you’re on a ${pageModel?.coverPeriod}-month policy. This is the total cost for the full term.`;
  }

  useEffect(() => {
    if ((isEligibleForMonthlyFlex) && selected.length === 1) {
      const isMonthly = selected[0].id === 'monthly';
      dispatch(setMonthlyFlex(isMonthly));
    }
  }, [selected]);

  return (
    <>
      <h5>{heading}</h5>
      <p>{description}</p>
      <GreenCheckboxList
        options={visibleOptions}
        selected={selected}
        setSelected={setSelected}
        singleSelect
      />
      {((isEligibleForMonthlyFlex && !isEurSelected) || pageModel?.isMonthlyLongerTermPolicy)
        && <p><small>We can’t always split the cost of your cover into equal monthly payments, but you’ll see a full breakdown on the final step.</small></p>}
    </>
  );
};

export default BasketOptions;
